/* eslint-disable @nx/enforce-module-boundaries */
import { EditableComponent } from '@adobe/aem-react-editable-components';
import React, { useEffect, useId, useRef } from 'react';
import dynamic from 'next/dynamic';
import { AIcon, AIconSize, MTag, MTagType, ABody, ABodySize, ABodyColor } from '@marriott/mi-web-ui-kit';
import * as Styled from './ONavigationSide.styles';
import { ONavigationSideProps } from './ONavigationSide.types';
import { useChatSessionActions, useMarriottAIPreviewStore, useUIActions, useUIState } from '../../store';
import { MListNavActions } from '../../molecules/MListNavActions';
import { MNavUtilityItem } from '../../molecules/MNavUtilityItem';
import { HiddenPrivacyFooterHosts } from './HiddenPrivacyFooterHosts';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { usePageContext } from '@marriott/mi-shop-components';
import { AI_SEARCH_TRACK_LABELS } from '../../constants/aiSearchTrackLabels';
import { MAX_CHAT_COUNT } from '../../utils/indexedDBStorage';
import { useDialogFocusTrap } from '../../hooks/useDialogFocusTrap';
import SparkleLogo from './sparkle.png';
import clsx from 'clsx';

const SIDEBAR_NAV_ID = 'chat-sidebar-navigation';

const MConversationalNavGroupVerticalComp = dynamic(
  () => import('../../molecules/MConversationalNavGroupVertical').then(mod => mod.MConversationalNavGroupVerticalComp),
  { ssr: false }
);

interface StaticImageData {
  src: string;
  height: number;
  width: number;
  placeholder?: string;
}

type ImageImport = string | StaticImageData;

const getImageSrc = (img: ImageImport): string => {
  return typeof img === 'string' ? img : img.src;
};
const DEFAULT_SSE_BASE_URL = 'https://orchestratorsvc.emergingtech.dev2.cld.marriott.com';
export const ONavigationSideComp: React.FC<ONavigationSideProps> = ({ showChatHistory }) => {
  const { setShowChat, setIsSidebarOpen, setIsNewChatMode, setIsSettingsOpen } = useUIActions();
  const { isSidebarOpen, isMobile, isSettingsOpen } = useUIState();
  const { createNewChat, getRecentChats, updateChatTitle, deleteChat } = useChatSessionActions();
  const recentChats = getRecentChats();
  const currentChatId = useMarriottAIPreviewStore(state => state.currentChatId);
  const switchToChat = useMarriottAIPreviewStore(state => state.switchToChat);
  const IS_LOCAL_DEV = useClientEnvVarsStore(state => state.envVarsObject['IS_LOCAL_DEV']);
  const recentChatsHeadingId = useId();
  const isAuthenticatedFromStore = useMarriottAIPreviewStore(state => state.authenticated);
  const { isOffline = false } = usePageContext();
  const isAuthenticated = !isOffline && (IS_LOCAL_DEV === 'true' || isAuthenticatedFromStore);
  const isMobileDrawerOpen = isAuthenticated && isMobile && isSidebarOpen;
  const sidebarPanelRef = useRef<HTMLDivElement>(null);
  const sidebarCloseButtonRef = useRef<HTMLButtonElement>(null);

  useDialogFocusTrap(isMobileDrawerOpen, sidebarPanelRef, {
    initialFocusRef: sidebarCloseButtonRef,
  });

  // Utility function for relative time
  const formatRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) return 'now';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInHours < 24) return `${diffInHours}h`;
    if (diffInDays < 7) return `${diffInDays}d`;
    return date.toLocaleDateString();
  };

  const handleSwitchToChat = (chatId: string) => {
    if (chatId !== currentChatId) {
      switchToChat(chatId);
      setShowChat(true);
      setIsSidebarOpen(false); // Close sidebar on mobile
    }
  };

  // Close settings panel when sidebar closes
  useEffect(() => {
    if (!isSidebarOpen) {
      setIsSettingsOpen(false);
    }
  }, [isSidebarOpen, setIsSettingsOpen]);

  const onNewChat = () => {
    // Get current chat sessions and filter to non-empty chats
    const chatSessions = useMarriottAIPreviewStore.getState().chatSessions;
    const nonEmptyChats = chatSessions.filter(chat => chat.messageCount > 0);
    const maxChatCount = Number(useClientEnvVarsStore.getState().envVarsObject['MAX_CHAT_COUNT']) || MAX_CHAT_COUNT;

    // Auto-delete oldest chat if at max capacity
    if (nonEmptyChats.length >= maxChatCount) {
      const oldestChat = [...nonEmptyChats].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )[0];
      if (oldestChat) {
        deleteChat(oldestChat.id);
      }
    }

    setShowChat(true);
    if (isMobile) {
      setIsSidebarOpen(false);
    }
    createNewChat();
    setIsNewChatMode(true);
  };

  const handleOpenClick = () => {
    if (isAuthenticated) {
      setIsSidebarOpen(!isSidebarOpen);
    }
  };

  const handleSettingsClick = () => {
    setIsSettingsOpen(!isSettingsOpen);
  };

  return (
    <>
      {isMobileDrawerOpen && (
        <Styled.MobileOverlay type="button" onClick={() => setIsSidebarOpen(false)} aria-label="Close sidebar" />
      )}
      <Styled.Wrapper
        ref={sidebarPanelRef}
        id={SIDEBAR_NAV_ID}
        expanded={isSidebarOpen}
        $enableHoverActions={isAuthenticated}
        className={clsx(isSidebarOpen ? 'p-4' : 'py-4 px-3')}
        role={isMobileDrawerOpen ? 'dialog' : undefined}
        aria-modal={isMobileDrawerOpen ? true : undefined}
        aria-label={isMobileDrawerOpen ? 'Navigation menu' : undefined}
      >
        <Styled.LogoWrapper expanded={isSidebarOpen} $enableHoverActions={isAuthenticated}>
          <Styled.LogoContainer
            type="button"
            onClick={isAuthenticated ? handleOpenClick : undefined}
            disabled={!isAuthenticated}
            className="overflow-hidden"
            $isInteractive={isAuthenticated}
            aria-expanded={isAuthenticated ? isSidebarOpen : undefined}
            aria-controls={isAuthenticated ? SIDEBAR_NAV_ID : undefined}
            aria-label={isAuthenticated ? (isSidebarOpen ? 'Ask Bonvoy' : 'Ask Bonvoy, expand sidebar') : undefined}
          >
            <span className="d-flex align-items-center">
              <img
                src={getImageSrc(SparkleLogo)}
                className="logo-container"
                data-testid="logo"
                alt=""
                aria-hidden="true"
                style={{
                  width: isSidebarOpen ? '32px' : '32px',
                  height: '32px',
                  objectFit: 'contain',
                }}
              />
              {isSidebarOpen && (
                <Styled.BrandNameWrapper>
                  <Styled.BrandName className="t-subtitle-s">Ask Bonvoy</Styled.BrandName>
                  <MTag aLabel={{ text: 'Beta' }} type={MTagType.Alternate} />
                </Styled.BrandNameWrapper>
              )}
            </span>
          </Styled.LogoContainer>
          {isAuthenticated && !isSidebarOpen && (
            <Styled.ToggleButton
              type="button"
              className="toggle-button"
              onClick={() => setIsSidebarOpen(true)}
              aria-expanded={false}
              aria-controls={SIDEBAR_NAV_ID}
              aria-label="Expand sidebar to show chat history"
            >
              <AIcon icon={'icon-double-chevron-right'} aria-hidden="true"></AIcon>
            </Styled.ToggleButton>
          )}
          {isAuthenticated && isSidebarOpen && (
            <Styled.ToggleButton
              ref={isMobile ? sidebarCloseButtonRef : undefined}
              type="button"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              aria-expanded={true}
              aria-controls={SIDEBAR_NAV_ID}
              aria-label={isMobile ? 'Close sidebar' : 'Collapse sidebar'}
            >
              <AIcon
                icon={isMobile ? 'icon-clear' : 'icon-double-chevron-left'}
                size={isMobile ? AIconSize.L : AIconSize.S}
                aria-hidden="true"
              ></AIcon>
            </Styled.ToggleButton>
          )}
        </Styled.LogoWrapper>
        {isAuthenticated && (
          <>
            <HiddenPrivacyFooterHosts />
            <div className="new-chat-item">
              <MNavUtilityItem
                icon={{ icon: 'icon-new-chat', size: AIconSize.M }}
                buttonLabel="New Chat"
                onClick={() => onNewChat()}
                expanded={isSidebarOpen}
                trackLabel={AI_SEARCH_TRACK_LABELS.NEW_CHAT}
              />
            </div>
            {showChatHistory === 'true' && isSidebarOpen && (
              <Styled.ChatHistoryWrapper
                className="align-self-start d-flex flex-column"
                role="region"
                aria-labelledby={recentChatsHeadingId}
                data-track-label={AI_SEARCH_TRACK_LABELS.CHAT_HISTORY}
              >
                <Styled.ChatHistoryHeading id={recentChatsHeadingId} className="t-subtitle-m" expanded={isSidebarOpen}>
                  Recent Chats
                </Styled.ChatHistoryHeading>
                <ABody
                  bodyText="Latest chats from the past 5 days. Prices and availability may no longer be current."
                  size={ABodySize.XS}
                  color={ABodyColor.Alternate}
                />

                <div>
                  <Styled.ChatList expanded={isSidebarOpen} aria-label="Recent chats list">
                    {recentChats.map(chat => (
                      <MListNavActions
                        key={chat.id}
                        chat={chat}
                        isActive={chat.id === currentChatId}
                        onSwitch={handleSwitchToChat}
                        onRename={updateChatTitle}
                        onDelete={deleteChat}
                        formatRelativeTime={formatRelativeTime}
                      />
                    ))}
                    {recentChats.length === 0 && (
                      <li
                        style={{
                          padding: '20px 16px',
                          textAlign: 'center',
                          color: '#707070',
                          fontSize: '14px',
                        }}
                      >
                        No recent chats
                      </li>
                    )}
                  </Styled.ChatList>
                </div>
              </Styled.ChatHistoryWrapper>
            )}
            <div className="settings-item">
              <MNavUtilityItem
                icon={{ icon: 'icon-gear', size: isMobile ? AIconSize.L : AIconSize.M }}
                rightIcon={isMobile ? { icon: 'icon-arrow-right', size: AIconSize.M } : undefined}
                buttonLabel="Settings"
                expanded={isSidebarOpen}
                alignment="bottom"
                onClick={handleSettingsClick}
              />
            </div>
            {isSettingsOpen && <MConversationalNavGroupVerticalComp renderAsOverlay={true} />}
          </>
        )}
      </Styled.Wrapper>
    </>
  );
};

export const ONavigationSideConfig = {
  emptyLabel: 'ONavigationSide',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/o-navigation-side`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ONavigationSide = (props: any) => {
  const model = props.model || {};

  return (
    <EditableComponent config={ONavigationSideConfig}>
      <ONavigationSideComp {...model} />
    </EditableComponent>
  );
};
